import type { FormEvent } from "react";
import { Button, Input, Label } from "@panelgenie/shared-ui";
import {
  INVOCATION_KINDS,
  INVOCATION_OUTCOMES,
  type InvocationKind,
  type InvocationOutcome,
} from "../types.js";
import { kindLabel, outcomeLabel } from "../lib/format.js";

/**
 * Controlled filter bar over the `support.listInvocations` query — user
 * id (exact), kind, and outcome. Empty string means "no filter" for the
 * two selects (the route omits the field from the query input). The bar
 * is fully controlled: it owns no state, so the route can drive the
 * query key off the same `value` and tests can assert on props.
 */

export type FilterState = {
  readonly userId: string;
  readonly kind: InvocationKind | "";
  readonly outcome: InvocationOutcome | "";
};

export const EMPTY_FILTERS: FilterState = {
  userId: "",
  kind: "",
  outcome: "",
};

export type InvocationFiltersProps = {
  readonly value: FilterState;
  readonly onChange: (next: FilterState) => void;
  /** Fired when the user submits the bar (Enter or the Apply button). */
  readonly onApply: () => void;
  readonly onClear: () => void;
  readonly busy?: boolean;
};

const selectClass =
  "h-11 w-full rounded-md border border-line bg-elevated px-3 text-base text-fg " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1";

export function InvocationFilters({
  value,
  onChange,
  onApply,
  onClear,
  busy = false,
}: InvocationFiltersProps) {
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onApply();
  };

  return (
    <form
      aria-label="invocation-filters"
      onSubmit={submit}
      className="flex flex-wrap items-end gap-3"
    >
      <div className="flex min-w-56 flex-1 flex-col gap-1">
        <Label htmlFor="filter-userId">User ID</Label>
        <Input
          id="filter-userId"
          value={value.userId}
          placeholder="Exact Clerk user id"
          onChange={(e) => onChange({ ...value, userId: e.target.value })}
        />
      </div>

      <div className="flex min-w-44 flex-col gap-1">
        <Label htmlFor="filter-kind">Kind</Label>
        <select
          id="filter-kind"
          className={selectClass}
          value={value.kind}
          onChange={(e) =>
            onChange({ ...value, kind: e.target.value as FilterState["kind"] })
          }
        >
          <option value="">All kinds</option>
          {INVOCATION_KINDS.map((kind) => (
            <option key={kind} value={kind}>
              {kindLabel(kind)}
            </option>
          ))}
        </select>
      </div>

      <div className="flex min-w-44 flex-col gap-1">
        <Label htmlFor="filter-outcome">Outcome</Label>
        <select
          id="filter-outcome"
          className={selectClass}
          value={value.outcome}
          onChange={(e) =>
            onChange({
              ...value,
              outcome: e.target.value as FilterState["outcome"],
            })
          }
        >
          <option value="">All outcomes</option>
          {INVOCATION_OUTCOMES.map((outcome) => (
            <option key={outcome} value={outcome}>
              {outcomeLabel(outcome)}
            </option>
          ))}
        </select>
      </div>

      <div className="flex gap-2">
        <Button type="submit" variant="primary" disabled={busy}>
          Apply
        </Button>
        <Button type="button" variant="secondary" onClick={onClear}>
          Clear
        </Button>
      </div>
    </form>
  );
}
